import Vue from 'vue'
import api from '@/api/api.js'
import { DataLoadState } from '@/utils/storeUtils.js'

// Mutation names
const SET_LOAD_HUNT_AREA_INFO_STARTED = 'setLoadHuntAreaInfoStarted'
const SET_LOAD_HUNT_AREA_INFO_FINISHED_SUCCESSFULLY = 'setLoadHuntAreaInfoFinishedSuccessfully'
const SET_LOAD_HUNT_AREA_INFO_FAILED = 'setLoadHuntAreaInfoFailed'
const CLEAR_CACHE = 'clearCache'

// Getter names
export const IS_LOADING_HUNT_AREA_INFO_FOR_HUNT_AREA_ID = 'isLoadingHuntAreaInfoForHuntAreaId'
export const GET_HUNT_AREA_INFO_FOR_HUNT_AREA_ID = 'getHuntAreaInfoForHuntAreaId'
export const GET_HUNT_AREA_INFO_LOAD_ERROR_FOR_HUNT_AREA_ID = 'getHuntAreaInfoLoadErrorForHuntAreaId'

// Action name
export const LOAD_HUNT_AREA_INFO_FOR_HUNT_AREA_IDS = 'loadHuntAreaInfoForHuntAreaIds'
export const RESET_HUNT_AREAS_CACHE = 'reset'

// Hunt areas cache store implementation
export const HuntAreasCacheStore = {
  namespaced: true,

  state: () => ({ // state must be a function to allow reuse of this store
    huntAreaInfosCache: {}
  }),

  mutations: {
    [SET_LOAD_HUNT_AREA_INFO_STARTED] (state, { huntAreaId }) {
      const dataLoadState = new DataLoadState(huntAreaId)
      Vue.set(state.huntAreaInfosCache, huntAreaId, dataLoadState.stateForLoadStarted())
    },

    [SET_LOAD_HUNT_AREA_INFO_FINISHED_SUCCESSFULLY] (state, { huntAreaInfo }) {
      const huntAreaId = huntAreaInfo.huntAreaId
      const dataLoadState = state.huntAreaInfosCache[huntAreaId] || new DataLoadState(huntAreaId)
      Vue.set(state.huntAreaInfosCache, huntAreaId, dataLoadState.stateForLoadFinished(huntAreaInfo))
    },

    [SET_LOAD_HUNT_AREA_INFO_FAILED] (state, { huntAreaId, error }) {
      const dataLoadState = state.huntAreaInfosCache[huntAreaId]
      Vue.set(state.huntAreaInfosCache, huntAreaId, dataLoadState.stateForLoadFailed(error))
    },

    [CLEAR_CACHE] (state) {
      state.huntAreaInfosCache = {}
    }
  },

  getters: {
    [IS_LOADING_HUNT_AREA_INFO_FOR_HUNT_AREA_ID]: state => huntAreaId => {
      const dataLoadState = state.huntAreaInfosCache[huntAreaId]
      if (!dataLoadState) {
        return false
      }

      return dataLoadState.status.loading
    },

    [GET_HUNT_AREA_INFO_FOR_HUNT_AREA_ID]: state => huntAreaId => {
      const dataLoadState = state.huntAreaInfosCache[huntAreaId]
      if (!dataLoadState) {
        return null
      }

      return dataLoadState.data
    },

    [GET_HUNT_AREA_INFO_LOAD_ERROR_FOR_HUNT_AREA_ID]: state => huntAreaId => {
      const dataLoadState = state.huntAreaInfosCache[huntAreaId]
      if (!dataLoadState) {
        return null
      }

      return dataLoadState.status.error
    }
  },

  actions: {
    [LOAD_HUNT_AREA_INFO_FOR_HUNT_AREA_IDS]: async ({ state, commit }, { huntAreaIds }) => {
      const filteredHuntAreaIds = huntAreaIds
        .filter(huntAreaId => huntAreaId !== null && huntAreaId !== undefined)
        .filter(huntAreaId => !state.huntAreaInfosCache[huntAreaId] || state.huntAreaInfosCache[huntAreaId].status.fail === true)

      const uniqueHuntAreaIds = [...new Set(filteredHuntAreaIds)]

      if (uniqueHuntAreaIds.length === 0) {
        return
      }

      uniqueHuntAreaIds.forEach(huntAreaId => {
        commit(SET_LOAD_HUNT_AREA_INFO_STARTED, { huntAreaId })
      })

      try {
        const response = await api.post('admin/huntareas/info', { huntAreaIds: uniqueHuntAreaIds })
        const huntAreaInfos = response.data

        huntAreaInfos.forEach(huntAreaInfo => {
          commit(SET_LOAD_HUNT_AREA_INFO_FINISHED_SUCCESSFULLY, { huntAreaInfo })
        })

        // Handle hunt areas that were not found
        uniqueHuntAreaIds
          .filter(huntAreaId => state.huntAreaInfosCache[huntAreaId].status.success === false)
          .forEach(huntAreaId => {
            commit(SET_LOAD_HUNT_AREA_INFO_FINISHED_SUCCESSFULLY, { huntAreaInfo: { huntAreaId, hasBorder: false, markersCount: 0 } })
          })
      } catch (error) {
        uniqueHuntAreaIds.forEach(huntAreaId => {
          commit(SET_LOAD_HUNT_AREA_INFO_FAILED, { huntAreaId, error })
        })
      }
    },

    [RESET_HUNT_AREAS_CACHE]: async ({ commit }) => {
      commit(CLEAR_CACHE)
    }
  }
}
